/**
 * Gmail as a collection.
 *
 * Every call here goes straight from the browser to googleapis.com, which sends
 * CORS headers for the Gmail API. No message, subject or address ever reaches
 * the Eventera backend.
 */

import { SiGmail } from "react-icons/si";

import { htmlToText } from "./html";
import { mapPool } from "./pool";
import {
  ExternalItem,
  ExternalProviderModule,
  ProviderConfig,
  ProviderDetailContext,
  ProviderFetchContext,
} from "./types";

const API = "https://www.googleapis.com/gmail/v1/users/me";

/**
 * `gmail.readonly`, not `gmail.metadata`: the metadata scope forbids the `q`
 * parameter outright, and `q` is the only way to bound a query by date. There
 * is no narrower scope that can answer "what arrived on this day".
 */
export const GMAIL_SCOPE = "https://www.googleapis.com/auth/gmail.readonly";

/**
 * How many message fetches are in flight at once.
 *
 * messages.list returns ids only, so the headers cost one request per message.
 * Google's batch endpoint would collapse those into one, but it speaks
 * multipart/mixed and has to be hand-assembled and hand-parsed; a small pool is
 * a great deal less machinery for the same wall-clock time at these counts.
 */
const FETCH_CONCURRENCY = 8;

interface GmailListResponse {
  messages?: { id: string }[];
  nextPageToken?: string;
  resultSizeEstimate?: number;
}

interface GmailPart {
  mimeType?: string;
  body?: { data?: string; size?: number };
  parts?: GmailPart[];
  headers?: { name: string; value: string }[];
}

interface GmailMessage {
  id: string;
  threadId?: string;
  snippet?: string;
  payload?: GmailPart;
}

/**
 * Gmail encodes part bodies base64url, and the bytes are whatever charset the
 * sender used. Decoding to a binary string and then running it through
 * TextDecoder is what keeps a UTF-8 subject line from arriving as mojibake.
 */
function decodeBase64Url(data: string): string {
  const binary = atob(data.replace(/-/g, "+").replace(/_/g, "/"));
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder("utf-8").decode(bytes);
}

/** The first part of a given type, depth first. */
function findPart(
  part: GmailPart | undefined,
  mimeType: string,
): GmailPart | null {
  if (!part) return null;
  if (part.mimeType === mimeType && part.body?.data) return part;
  for (const child of part.parts ?? []) {
    const found = findPart(child, mimeType);
    if (found) return found;
  }
  return null;
}

function query(config: ProviderConfig, from: Date, to: Date) {
  // Epoch seconds, not the YYYY/MM/DD form: Gmail accepts both, but the date
  // form rounds to whole days in the user's timezone, which makes an hour view
  // return the whole day around it.
  const after = Math.floor(from.getTime() / 1000);
  const before = Math.ceil(to.getTime() / 1000);
  const base = typeof config.q === "string" ? config.q.trim() : "";
  return `${base} after:${after} before:${before}`.trim();
}

/** The only two things any Gmail call needs from its caller's context. */
type GmailAuth = Pick<ProviderFetchContext, "accessToken" | "signal">;

async function gmailFetch<T>(
  path: string,
  params: URLSearchParams,
  auth: GmailAuth,
): Promise<T> {
  const response = await fetch(`${API}${path}?${params}`, {
    headers: { Authorization: `Bearer ${auth.accessToken}` },
    signal: auth.signal,
  });
  if (!response.ok) {
    // 401/403 reach the loader as a plain error and are reported per source;
    // the token broker is what turns a dead *grant* into a reconnect prompt.
    throw new Error(`Gmail request failed (${response.status})`);
  }
  return (await response.json()) as T;
}

function header(message: GmailMessage, name: string): string {
  const found = message.payload?.headers?.find(
    (h) => h.name.toLowerCase() === name.toLowerCase(),
  );
  return found?.value ?? "";
}

/** Strips the angle-bracket address, leaving the display name when there is one. */
function senderName(from: string): string {
  const match = from.match(/^\s*"?([^"<]*?)"?\s*<.+>\s*$/);
  const name = match?.[1]?.trim();
  return name || from.trim();
}

async function listIds(ctx: ProviderFetchContext): Promise<string[]> {
  const ids: string[] = [];
  let pageToken: string | undefined;

  // One list call per page, not the day-by-day sweep the earlier experiment
  // used -- Gmail bounds the whole range in a single query.
  while (ids.length < ctx.limit) {
    const params = new URLSearchParams({
      q: query(ctx.config, ctx.from, ctx.to),
      maxResults: String(Math.min(500, ctx.limit - ids.length)),
    });
    if (pageToken) params.set("pageToken", pageToken);

    const page = await gmailFetch<GmailListResponse>("/messages", params, ctx);
    for (const message of page.messages ?? []) ids.push(message.id);

    pageToken = page.nextPageToken;
    if (!pageToken) break;
  }

  return ids.slice(0, ctx.limit);
}

async function fetchDetails(
  ids: string[],
  ctx: ProviderFetchContext,
): Promise<ExternalItem[]> {
  const params = new URLSearchParams({ format: "metadata" });
  for (const name of ["Date", "Subject", "From"]) {
    params.append("metadataHeaders", name);
  }

  // The pool keeps the list in the order Gmail ranked it, which is the order
  // the timeline wants and not the order the requests happen to finish in.
  return mapPool(
    ids,
    FETCH_CONCURRENCY,
    async (id) => {
      const message = await gmailFetch<GmailMessage>(
        `/messages/${encodeURIComponent(id)}`,
        params,
        ctx,
      );
      const date = new Date(header(message, "Date"));
      // A message whose Date header will not parse has no place on a timeline;
      // the adapter drops it, but bailing here saves building it at all.
      if (Number.isNaN(date.getTime())) return null;

      const from = header(message, "From");
      return {
        externalId: message.id,
        title: header(message, "Subject") || "(no subject)",
        content: [senderName(from), message.snippet]
          .filter(Boolean)
          .join(" — "),
        at: date,
      };
    },
    ctx.signal,
  );
}

export const gmail: ExternalProviderModule = {
  key: "gmail",
  systemId: "google",
  title: "Gmail",
  description: "Your email, on the timeline. Read-only.",
  Icon: SiGmail,
  scopes: [GMAIL_SCOPE],
  // Deliberately a partition, not an overlapping set. Gmail files a promotion
  // in the inbox *and* in its category, so a plain `in:inbox` child alongside
  // `category:promotions` would list the same message under both. The
  // exclusions are what make each message appear exactly once.
  naturalChildren: [
    {
      title: "Received",
      providerKey: "gmail",
      config: {
        q: "in:inbox -category:social -category:promotions -category:updates -category:forums",
      },
    },
    { title: "Sent", providerKey: "gmail", config: { q: "in:sent" } },
    { title: "Social", providerKey: "gmail", config: { q: "category:social" } },
    {
      title: "Promotions",
      providerKey: "gmail",
      config: { q: "category:promotions" },
    },
    {
      title: "Updates",
      providerKey: "gmail",
      config: { q: "category:updates" },
    },
    { title: "Forums", providerKey: "gmail", config: { q: "category:forums" } },
  ],
  limits: {
    // Gmail did not exist before 2004, but the scale's own floor is the Unix
    // epoch, and nothing is gained by being cleverer than the data.
    earliest: new Date(0),
    // A month of mail is still readable as a list; a year is not, and asking
    // for one would be hundreds of requests for something nobody scrolls.
    maxSpanDays: 31,
    maxItems: 150,
  },
  transport: "client",

  async fetchItems(ctx: ProviderFetchContext): Promise<ExternalItem[]> {
    const ids = await listIds(ctx);
    if (ids.length === 0) return [];
    return fetchDetails(ids, ctx);
  },

  async fetchDetail(ctx: ProviderDetailContext): Promise<string> {
    const message = await gmailFetch<GmailMessage>(
      `/messages/${encodeURIComponent(ctx.externalId)}`,
      new URLSearchParams({ format: "full" }),
      ctx,
    );

    // A single-part plain message puts its body on the payload itself rather
    // than in a `parts` array, which findPart handles by testing the root.
    const plain = findPart(message.payload, "text/plain");
    if (plain?.body?.data) return decodeBase64Url(plain.body.data);

    const html = findPart(message.payload, "text/html");
    if (html?.body?.data) return htmlToText(decodeBase64Url(html.body.data));

    // Nothing readable -- an attachment-only message, say. The snippet the row
    // already carries is better than an empty pane.
    return message.snippet ?? "";
  },

  itemUrl(item: ExternalItem) {
    return `https://mail.google.com/mail/u/0/#all/${item.externalId}`;
  },
};
